import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getVisitorId } from "@/lib/affiliate";

const CODE_RE = /^[a-zA-Z0-9_-]{3,40}$/;
const SAFE_DEST = /^\/[a-zA-Z0-9\-_/]*$/;
/** First-touch attribution window: a stored code wins for 90 days. */
const ATTRIBUTION_WINDOW_MS = 90 * 86_400_000;

export const Route = createFileRoute("/affiliate/$code")({
  component: AffiliateLinkPage,
  head: () => ({
    meta: [
      { title: "Redirecting to Inquira" },
      { name: "description", content: "You followed an Inquira creator link. Redirecting you now." },
      { property: "og:title", content: "Inquira — AI homework help" },
      { property: "og:description", content: "Snap a problem, get clean step-by-step explanations." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

/**
 * First-touch attribution entry point: /affiliate/<code>
 * Logs the click server-side, stores the code locally, then forwards the visitor on.
 */
function AffiliateLinkPage() {
  const { code } = Route.useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const rawDest = params.get("to") ?? "/";
      const dest = SAFE_DEST.test(rawDest) ? rawDest : "/";
      const normalized = code.toLowerCase();

      if (CODE_RE.test(normalized)) {
        try {
          // First-touch wins: never overwrite a stored code that is still inside the window.
          const storedCode = localStorage.getItem("inq_aff_code");
          const storedAt = Number(localStorage.getItem("inq_aff_click_at") ?? 0);
          const storedIsValid =
            !!storedCode && CODE_RE.test(storedCode) && Number.isFinite(storedAt) && Date.now() - storedAt < ATTRIBUTION_WINDOW_MS;
          if (!storedIsValid) {
            localStorage.setItem("inq_aff_code", normalized);
            localStorage.setItem("inq_aff_click_at", String(Date.now()));
          }
          await supabase.rpc("record_affiliate_click" as never, {
            _code: normalized,
            _visitor_id: getVisitorId(),
            _landing_page: dest,
            _referrer: document.referrer || undefined,
            _source: params.get("utm_source") ?? undefined,
            _medium: params.get("utm_medium") ?? undefined,
            _campaign: params.get("utm_campaign") ?? undefined,
            _coupon_code: params.get("coupon") ?? undefined,
            _redirect_to: dest,
          } as never);
        } catch {
          /* attribution must never block the redirect */
        }
      }
      if (cancelled) return;
      const attributed = localStorage.getItem("inq_aff_code") ?? normalized;
      void navigate({ to: dest, search: { ref: attributed } as never, replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
      <h1 className="text-xl font-semibold">Taking you to Inquira…</h1>
      <p className="text-sm text-muted-foreground">One moment while we set up your creator link.</p>
    </div>
  );
}